<template>
    <b-container fluid class="overview">
        <h1 class="text-left">{{ title }}</h1>
        <br />
        <div class="row">
            <!-- Csv export -->
            <csv-export
                :disabled="noData"
                :dataArray="csvData"
                :fileName="`${$t('menuItemUsers').toLowerCase()}.csv`"
                :fieldSeparator="';'"
                :title="$t('labelDownloadData')"
                class="form-group pl-3"
            >
            </csv-export>
            <!-- User sync with Azure AD-->
            <div v-if="organizationHasAzureTenant" class="form-group pl-3">
                <b-button
                    id="sync_users"
                    variant="primary"
                    @click="$_syncUsers"
                    :disabled="isGenerating"
                >
                    <span
                        class="fas"
                        :class="{
                            'fa-check': !isGenerating,
                            'fa-sync-alt fa-spin': isGenerating
                        }"
                    >
                    </span
                    >&nbsp;{{ $t('users.labelSync') }}
                </b-button>
            </div>
            <!-- Selected item actions
                 if some or all are selected
            -->
            <div v-if="isRowSelected" class="form-group pl-3">
                <user-selection
                    :selectedUsers="filteredRecords"
                    :userGroups="userGroups"
                    @reloadUsers="$_init()"
                />
            </div>
        </div>
        <div class="row">
            <!-- Search field -->
            <search-box
                id="searchUsers"
                @input="$_onInput"
                :info="$t('searchInfo')"
                :autofocus="true"
                :value="searchText"
                :saveInState="true"
            >
            </search-box>
            <!-- Active time filter field -->
            <x-select
                :id="`selectLastUsed`"
                :name="'ddActiveTime'"
                class="pr-3"
                :options="getTimeOptions"
                :textDefaultOption="$t('labelLastUsed')"
                v-model="selectedActiveTime"
                :saveInState="true"
            >
            </x-select>
            <x-select
                :id="`userGroup`"
                :name="'userGroup'"
                :options="userGroupOptions"
                class="pr-3"
                :textDefaultOption="`${$t('filterOn')} ${$t(
                    'labelUserGroup'
                ).toLowerCase()}`"
                v-model="selectedUserGroup"
                :simpleMode="true"
                :saveInState="true"
            >
            </x-select>
            <x-select
                :id="`profileBackground`"
                :name="'profileBackground'"
                :options="profileBackgroundOptions"
                class="pr-3"
                :textDefaultOption="`${$t('filterOn')} ${$t(
                    'labelProfileBaseLayer'
                ).toLowerCase()}`"
                v-model="selectedBackgroundProfile"
                :simpleMode="true"
                :saveInState="true"
            >
            </x-select>
            <x-select
                :id="`profileTheme`"
                :name="'profileTheme'"
                class="pr-3"
                :options="profileThemeOptions"
                :textDefaultOption="`${$t('filterOn')} ${$t(
                    'labelProfileTheme'
                ).toLowerCase()}`"
                v-model="selectedThemeProfile"
                :simpleMode="true"
                :saveInState="true"
            >
            </x-select>
            <x-select
                :id="`profileApi`"
                :name="'profileApi'"
                :options="profileApiOptions"
                :textDefaultOption="`${$t('filterOn')} ${$t(
                    'labelProfileApi'
                )}`"
                v-model="selectedApiProfile"
                :simpleMode="true"
                :saveInState="true"
            >
            </x-select>
            <b-link class="pl-3 pr-5 pt-2" @click="$_clearFilters()">
                {{ $t('clearFilters') }}
            </b-link>
        </div>
        <!-- Overview Table -->
        <div>
            <b-table
                striped
                hover
                small
                outlined
                bordered
                sticky-header="60vh"
                v-show="dataLoaded"
                @row-clicked="$_onDetails"
                :tbody-tr-class="$_rowClass"
                :items="filteredRecords"
                :fields="filteredFields"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                sort-icon-left
            >
                <template #cell(userProxyUsers)="data">
                    <span v-html="data.value"></span>
                </template>
                <template v-slot:cell(selected)="row">
                    <div
                        @click.stop
                        class="h-100 w-100 d-flex align-items-center justify-content-center pl-1"
                    >
                        <b-form-checkbox
                            :id="`${row.item.id}selected`"
                            :name="`${row.item.id}selected`"
                            v-model="row.item.selected"
                            @input="$_onRowSelected()"
                        >
                        </b-form-checkbox>
                    </div>
                </template>
                <template v-slot:head(selected)="">
                    <x-checkbox
                        id="selectAll"
                        name="selectAll"
                        :state="null"
                        :value="isAllSelected"
                        @change="$_onSelectAll($event)"
                        :indeterminate="isIndeterminate"
                        :expandClickArea="true"
                        class="pl-1"
                    >
                    </x-checkbox>
                </template>
            </b-table>
        </div>
        <!-- No content for overview -->
        <div v-show="noData" class="col-md-12">
            <span>{{ $t('userNonePresent') }}</span>
        </div>
        <router-links-footer :routerLinks="[{ name: 'userDetailNew' }]" />
    </b-container>
</template>

<script>
    import SearchBox from '../../components/SearchBox.vue';
    import RouterLinksFooter from '../../components/dashboard/RouterLinksFooter.vue';
    import CsvExport from '../../components/CsvExport.vue';
    import XSelect from '../../components/BaseComponents/x-select.vue';
    import XCheckbox from '../../components/BaseComponents/x-checkbox.vue';
    import UserSelection from './UserSelection.vue';
    import {
        nprogress,
        convertToValueTextPair,
        arraySortAscendingFunction
    } from '../../services/utilities';
    import modalMixin from '../../components/Mixins/ModalMixin.vue';
    import { PROFILE_TYPE } from '../../constants';

    export default {
        mixins: [modalMixin],
        name: 'UsersOverview',

        components: {
            'search-box': SearchBox,
            'x-select': XSelect,
            'x-checkbox': XCheckbox,
            'router-links-footer': RouterLinksFooter,
            'csv-export': CsvExport,
            'user-selection': UserSelection
        },

        data: function () {
            return {
                sortBy: 'userName',
                sortDesc: false,
                fields: [
                    {
                        key: 'selected',
                        tdClass:
                            'user-chkbox-cell text-center align-middle p-0 m-0',
                        thClass: 'text-center align-middle'
                    },
                    {
                        key: 'userName',
                        label: this.$t('labelUsername'),
                        sortable: true,
                        thStyle: { border: 0 },
                        thClass: 'align-middle',
                        tdClass: 'align-middle'
                    },
                    {
                        key: 'userProxyUsers',
                        label: this.$t('labelUserProxyUsers'),
                        sortable: true,
                        thStyle: { border: 0 },
                        thClass: 'align-middle',
                        tdClass: 'align-middle',
                        formatter: (value) => {
                            return value.join('<br>');
                        }
                    },
                    {
                        key: 'isAdmin',
                        label: this.$t('labelAdminUser'),
                        sortable: true,
                        formatter: (value) => {
                            return value === 1 ? this.$t('RefcodeYes') : '';
                        },
                        thStyle: { border: 0 },
                        thClass: 'align-middle',
                        tdClass: 'align-middle'
                    },
                    {
                        key: 'lastUsedDate',
                        label: this.$t('labelLastUsed'),
                        sortable: true,
                        thStyle: { border: 0 },
                        thClass: 'align-middle',
                        tdClass: 'align-middle'
                    },
                    {
                        key: 'usageTime',
                        label: this.$t('usageTime'),
                        sortable: true,
                        thStyle: { border: 0 },
                        thClass: 'align-middle',
                        tdClass: 'align-middle',
                        formatter: (value) => {
                            if (value === 0) {
                                return '-';
                            } else {
                                return `${value} min.`;
                            }
                        }
                    },
                    {
                        key: 'deactivationDate',
                        label: this.$t('users.deactivationDate'),
                        sortable: true,
                        thStyle: { border: 0 },
                        thClass: 'align-middle',
                        tdClass: 'align-middle'
                    },
                    {
                        key: 'userGroupName',
                        label: this.$t('labelUserGroup'),
                        sortable: true,
                        thStyle: { border: 0 },
                        thClass: 'align-middle',
                        tdClass: 'align-middle'
                    },
                    {
                        key: 'backgroundProfiles',
                        label: this.$t('labelProfileBaseLayer'),
                        sortable: true,
                        thStyle: { border: 0 },
                        thClass: 'align-middle',
                        tdClass: 'align-middle'
                    },
                    {
                        key: 'themeProfiles',
                        label: this.$t('labelProfileTheme'),
                        sortable: true,
                        thStyle: { border: 0 },
                        thClass: 'align-middle',
                        tdClass: 'align-middle'
                    },
                    {
                        key: 'apiProfiles',
                        label: this.$t('labelProfileApi'),
                        sortable: true,
                        thStyle: { border: 0 },
                        thClass: 'align-middle',
                        tdClass: 'align-middle'
                    },
                    {
                        key: 'isSoftDeleted',
                        label: this.$t('labelIsSoftDeleted'),
                        sortable: true,
                        thStyle: { border: 0 },
                        thClass: 'align-middle',
                        tdClass: 'align-middle',
                        formatter: (value) => {
                            if (value) {
                                return 'Ja';
                            } else {
                                return null;
                            }
                        }
                    }
                ],
                records: [],
                recordsBackground: [],
                recordsTheme: [],
                recordsApi: [],
                searchText: '',
                selectedActiveTime: null,
                selectedBackgroundProfile: null,
                selectedThemeProfile: null,
                selectedApiProfile: null,
                selectedUserGroup: null,
                isResolved: false,
                isGenerating: false,
                organizationHasAzureTenant: false,
                organizationHasScimToken: false,
                userGroups: [],
                isRowSelected: false,
                showUserProxyUsers: false
            };
        },

        computed: {
            title() {
                if (this.dataLoaded) {
                    const firstUser = this.records[0];
                    if (
                        firstUser.cooperationName &&
                        firstUser.cooperationName.length > 0
                    ) {
                        return `${this.$t('menuItemUsers')} - ${this.$t(
                            'cooperation.title'
                        )}: ${firstUser.cooperationName}`;
                    }
                }
                return this.$t('menuItemUsers');
            },

            dataLoaded() {
                return this.records.length > 0;
            },
            noData() {
                return this.isResolved && this.records && this.records.length === 0;
            },
            // Show field userProxyUser when there is at least 1 record that has a userProxyUser value
            // Show isSoftdeleted for organizations with a Scim API
            filteredFields() {
                return this.fields.filter((f) => {
                    if (f.key === 'userProxyUsers' && !this.showUserProxyUsers) {
                        return false;
                    }
                    if (
                        f.key === 'isSoftDeleted' &&
                        !this.organizationHasScimToken
                    ) {
                        return false;
                    }
                    return true;
                });
            },
            filteredRecords() {
                let records = JSON.parse(JSON.stringify(this.records));

                // filter records on username
                records = this.$_filterByUserName(records);

                // filter records on time
                records = this.$_filterByTime(records);

                // filter on usergroup
                records = this.$_filterByUserGroup(records);

                // filter records on backgroundprofile
                records = this.$_filterByBackgroundProfile(records);

                // filter records on themeprofile
                records = this.$_filterByThemeProfile(records);

                // filter records on apiprofile
                records = this.$_filterByApiProfile(records);
                return records;
            },
            getTimeOptions() {
                const options = [];

                options.push({
                    text: `15 ${this.$t('labelMinutes')}`,
                    value: '15'
                });
                options.push({
                    text: `30 ${this.$t('labelMinutes')}`,
                    value: '30'
                });
                options.push({ text: `1 ${this.$t('labelHour')}`, value: '60' });
                options.push({ text: `2 ${this.$t('labelHour')}`, value: '120' });
                options.push({ text: `4 ${this.$t('labelHour')}`, value: '240' });
                options.push({ text: `8 ${this.$t('labelHour')}`, value: '480' });

                return options;
            },
            profileBackgroundOptions() {
                return convertToValueTextPair(
                    this.recordsBackground,
                    'name',
                    'name'
                );
            },
            profileThemeOptions() {
                return convertToValueTextPair(this.recordsTheme, 'name', 'name');
            },
            profileApiOptions() {
                return convertToValueTextPair(this.recordsApi, 'name', 'name');
            },
            userGroupOptions() {
                return convertToValueTextPair(this.userGroups, 'name', 'name');
            },
            csvData() {
                return [...this.filteredRecords]
                    .sort(arraySortAscendingFunction('userName'))
                    .map((item) => {
                        // Dynamically build the object based on fields definition
                        return this.fields.reduce((csvRow, field) => {
                            // Skip fields that don't have a label (e.g., 'selected')
                            if (field.label) {
                                const key = field.key;
                                let value = item[key];

                                // Apply any formatter if defined in the fields
                                if (field.formatter) {
                                    value = field.formatter(value);
                                }

                                // Use the translated label as the CSV header
                                csvRow[field.label] = value;
                            }
                            return csvRow;
                        }, {});
                    });
            },
            selectedUsers() {
                const users = this.filteredRecords.filter((fr) => fr.selected);
                return users;
            },
            isAllSelected() {
                return (
                    this.filteredRecords.length > 0 &&
                    this.filteredRecords.every(({ selected }) => selected === true)
                );
            },
            isIndeterminate() {
                return (
                    !this.isAllSelected &&
                    this.filteredRecords.some(({ selected }) => selected === true)
                );
            }
        },
        async created() {
            await this.$_init();
        },
        methods: {
            async $_init() {
                nprogress.start();
                // get all data from the backend
                let result = await this.$store.dispatch('useApiData', {
                    name: 'GetUserOverview'
                });

                this.records = result?.data;

                // Check if userProxyUsers have values, only then we show the column
                this.showUserProxyUsers = this.records.some(
                    (r) => r.userProxyUsers.length > 0
                );
                this.$_processRecords();

                await this.$_hasAzureTenant();

                // Only background profiles with id (profile_type_id) == 1
                result = await this.$store.dispatch('useApiData', {
                    name: 'GetProfileOverview',
                    parameters: {
                        profileTypeId: PROFILE_TYPE.BASELAYER
                    }
                });
                this.recordsBackground = result.data;

                // Only theme and storage layer profiles with id (profile_type_id) == 2
                result = await this.$store.dispatch('useApiData', {
                    name: 'GetProfileOverview',
                    parameters: {
                        profileTypeId: PROFILE_TYPE.THEME_FEEDBACK
                    }
                });
                this.recordsTheme = result.data;

                // Only api profiles with id (profile_type_id) == 3
                result = await this.$store.dispatch('useApiData', {
                    name: 'GetProfileOverview',
                    parameters: {
                        profileTypeId: PROFILE_TYPE.API
                    }
                });
                this.recordsApi = result.data;

                // get all data from the backend
                result = await this.$store.dispatch('useApiData', {
                    name: 'GetUserGroups'
                });
                this.userGroups = result.data;
                nprogress.done();
                this.isResolved = true;
            },
            $_onSelectAll(event) {
                this.filteredRecords.forEach((record) => {
                    record.selected = event;
                });
            },
            $_processRecords() {
                // sort records (lodash)
                if (this.records && this.records.length > 0) {
                    this.records.forEach((record) => {
                        // Profiles are empty when record has a userGroup. Joining empty array gives an error.
                        if (record.backgroundProfiles) {
                            record.backgroundProfiles =
                                record.backgroundProfiles.join(', ');
                        }
                        if (record.themeProfiles) {
                            record.themeProfiles = record.themeProfiles.join(', ');
                        }
                        if (record.apiProfiles) {
                            record.apiProfiles = record.apiProfiles.join(', ');
                        }
                        this.$_formatLastUsedDate(record);
                        this.$_formatDeactivationDate(record);

                        record.selected = false;
                    });
                }
            },
            async $_hasAzureTenant() {
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetOrganization',
                    parameters: {
                        id: this.$store.getters.userInfo.organizationId
                    }
                });

                // This determines if the Azure User Sync button is being shown or not.
                // If the organization has a Scim Token then do not show the button.
                if (result.data.azureAdTenant && !result.data.scimToken) {
                    this.organizationHasAzureTenant = true;
                }
                this.organizationHasScimToken = !!result.data.scimToken;
                console.log(
                    'organizationHasScimToken',
                    this.organizationHasScimToken
                );
            },
            $_onDetails(record) {
                this.$router.push({ path: `/dashboard/user/detail/${record.id}` });
            },
            $_onInput(searchText) {
                this.searchText = searchText;
            },
            $_rowClass() {
                return 'tablerow';
            },
            $_dateFilter(selectedActiveTime, lastUsedDate) {
                var currentDate = new Date();
                var userDate = new Date(lastUsedDate);

                var timeDifference = currentDate - userDate;
                var diffMinutes = Math.ceil(timeDifference / (60 * 1000));

                return diffMinutes <= selectedActiveTime;
            },
            $_filterByUserName(records) {
                if (this.searchText && this.searchText.length > 0) {
                    const tokens = this.searchText.toLowerCase();
                    records = records.filter((record) => {
                        return (
                            record.userName.toLowerCase().includes(tokens) ||
                            record.userProxyUsers.some((p) =>
                                p.toLowerCase().includes(tokens)
                            )
                        );
                    });
                }

                return records;
            },
            $_filterByTime(records) {
                if (this.selectedActiveTime && this.selectedActiveTime.length > 0) {
                    records = records.filter((record) => {
                        return this.$_dateFilter(
                            this.selectedActiveTime,
                            record.lastUsedDate
                        );
                    });
                }
                return records;
            },
            $_filterByUserGroup(records) {
                return records.filter((record) => {
                    if (!this.selectedUserGroup) {
                        return true;
                    }
                    if (!record.userGroupName) {
                        return false;
                    }
                    return record.userGroupName === this.selectedUserGroup;
                });
            },
            $_filterByBackgroundProfile(records) {
                return records.filter((record) => {
                    if (!this.selectedBackgroundProfile) {
                        return true;
                    }

                    if (!record.backgroundProfiles) {
                        return false;
                    }

                    return record.backgroundProfiles.includes(
                        this.selectedBackgroundProfile
                    );
                });
            },

            $_filterByThemeProfile(records) {
                return records.filter((record) => {
                    if (!this.selectedThemeProfile) {
                        return true;
                    }

                    if (!record.themeProfiles) {
                        return false;
                    }

                    return record.themeProfiles.includes(this.selectedThemeProfile);
                });
            },
            $_filterByApiProfile(records) {
                return records.filter((record) => {
                    if (!this.selectedApiProfile) {
                        return true;
                    }

                    if (!record.apiProfiles) {
                        return false;
                    }

                    return record.apiProfiles.includes(this.selectedApiProfile);
                });
            },
            $_formatDate(date) {
                const year = date.getFullYear();
                const month = `0${date.getMonth() + 1}`.slice(-2);
                const days = `0${date.getDate()}`.slice(-2);
                const hours = `0${date.getHours()}`.slice(-2);
                const minutes = `0${date.getMinutes()}`.slice(-2);
                return `${year}-${month}-${days} ${hours}:${minutes}`;
            },

            $_formatLastUsedDate(record) {
                let date = new Date(record.lastUsedDate);
                if (date.getFullYear() > 1950) {
                    record.lastUsedDate = this.$_formatDate(date);
                } else {
                    record.lastUsedDate = '-';
                }
            },
            $_formatDeactivationDate(record) {
                if (record.deactivationDate) {
                    const date = new Date(record.deactivationDate);

                    record.deactivationDate = this.$_formatDate(date);
                } else {
                    record.deactivationDate = '-';
                }
            },

            $_syncUsers() {
                this.msgBoxConfirm({
                    message: this.$t('users.startSyncConfirmation')
                }).then(async (confirmed) => {
                    if (confirmed) {
                        this.isGenerating = true;
                        const organizationId =
                            this.$store.getters.userInfo.organizationId;
                        try {
                            const syncResult = await this.$store.dispatch(
                                'useApiData',
                                {
                                    name: 'SynchronizeUsersWithAzureAd',
                                    parameters: { OrganizationId: organizationId }
                                }
                            );
                            const message = this.$t('users.syncResult', [
                                syncResult.data.gebruikersToegevoegd,
                                syncResult.data.gebruikersVerwijderMarkering,
                                syncResult.data.gebruikersVerwijderd,
                                syncResult.data
                                    .gebruikersOnterechtGemarkeerdVoorVerwijderen
                            ]);
                            const data = {
                                message: message,
                                variant: 'success',
                                title: 'Synchronisatie is gelukt',
                                autoHideDelay: 10000
                            };
                            this.showToast(data);
                            // Reload page after change in data
                            if (
                                syncResult.data.gebruikersToegevoegd ||
                                syncResult.data.gebruikersVerwijderMarkering ||
                                syncResult.data.gebruikersVerwijderd ||
                                syncResult.data
                                    .gebruikersOnterechtGemarkeerdVoorVerwijderen
                            ) {
                                window.setTimeout(function () {
                                    document.location.reload();
                                }, 2000);
                            }
                        } catch (error) {
                            let messageData;
                            const msg = error.message ?? error.detail;
                            messageData = {
                                message: msg,
                                variant: 'danger',
                                title: this.$t(error.title),
                                autoHideDelay: 8000
                            };
                            this.showToast(messageData);
                        }
                        this.isGenerating = false;
                    }
                });
            },
            $_clearFilters() {
                this.$_onInput('');
                this.selectedActiveTime = '';
                this.selectedBackgroundProfile = '';
                this.selectedThemeProfile = '';
                this.selectedApiProfile = '';
                this.selectedUserGroup = '';
            },
            $_clearSelection() {
                this.$_onSelectAll(false);
            },
            $_onRowSelected() {
                this.isRowSelected = this.filteredRecords.some(
                    ({ selected }) => selected === true
                );
            }
        },
        watch: {
            // Clear selection when any of the filter items changes
            searchText() {
                this.$_clearSelection();
            },
            selectedActiveTime() {
                this.$_clearSelection();
            },
            selectedUserGroup() {
                this.$_clearSelection();
            },
            selectedBackgroundProfile() {
                this.$_clearSelection();
            },
            selectedThemeProfile() {
                this.$_clearSelection();
            },
            selectedApiProfile() {
                this.$_clearSelection();
            }
        }
    };
</script>

<style>
.user-chkbox-cell {
    height: 1px;
    cursor: default;
}
</style>
